/**
 * Markdown Content Renderer
 *
 * Renders markdown content with sanitized HTML output.
 * Supports: bold, italic, code, lists, links with syntax highlighting.
 */

import { useMemo } from 'react';
import { marked } from 'marked';
import Prism from 'prismjs';
import { getStreamingMarkdownSplit } from '../utils/streamingMarkdown';

// Import common language support
import 'prismjs/components/prism-typescript';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-jsx';
import 'prismjs/components/prism-tsx';
import 'prismjs/components/prism-css';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-sql';
import 'prismjs/components/prism-yaml';
import 'prismjs/components/prism-markdown';

interface MarkdownContentProps {
  content: string;
  className?: string;
}

/** Props for streaming (partial) markdown: only complete segments are rendered as markdown. */
export interface StreamingMarkdownContentProps {
  content: string;
  className?: string;
}

// Configure marked for safe, modest rendering with syntax highlighting
marked.setOptions({
  gfm: true,
  breaks: true,
});

// Custom renderer with syntax highlighting and security
const renderer = new marked.Renderer();

// Links - add security attributes
renderer.link = ({ href, title, text }) => {
  // Block javascript: URLs
  if (href?.toLowerCase().startsWith('javascript:')) {
    return text;
  }
  const titleAttr = title ? ` title="${escapeHtml(title)}"` : '';
  return `<a href="${escapeHtml(href || '')}" target="_blank" rel="noopener noreferrer"${titleAttr}>${text}</a>`;
};

// Code blocks - add Prism highlighting
renderer.code = ({ text, lang }) => {
  const language = lang && Prism.languages[lang] ? lang : 'plaintext';
  const grammar = Prism.languages[language] || Prism.languages.plaintext;
  
  let highlighted: string;
  try {
    highlighted = grammar ? Prism.highlight(text, grammar, language) : escapeHtml(text);
  } catch {
    highlighted = escapeHtml(text);
  }
  
  return `<pre class="language-${language}"><code class="language-${language}">${highlighted}</code></pre>`;
};

marked.use({ renderer });

// Simple HTML escape for attributes
function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Sanitize HTML to remove dangerous elements and attributes
function sanitizeHtml(html: string): string {
  // Remove script tags and their content
  let sanitized = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  
  // Remove event handlers (onclick, onerror, etc.)
  sanitized = sanitized.replace(/\s+on\w+\s*=\s*["'][^"']*["']/gi, '');
  sanitized = sanitized.replace(/\s+on\w+\s*=\s*[^\s>]+/gi, '');
  
  // Remove javascript: URLs in href/src attributes
  sanitized = sanitized.replace(/href\s*=\s*["']javascript:[^"']*["']/gi, 'href="#"');
  sanitized = sanitized.replace(/src\s*=\s*["']javascript:[^"']*["']/gi, 'src=""');
  
  return sanitized;
}

export function MarkdownContent({ content, className = '' }: MarkdownContentProps) {
  const html = useMemo(() => {
    const rawHtml = marked.parse(content, { async: false }) as string;
    return sanitizeHtml(rawHtml);
  }, [content]);

  return (
    <div
      className={`prose prose-sm prose-zinc dark:prose-invert max-w-none ${className}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

/**
 * For streaming content: only the "safe" part (complete tags + full lines) is
 * rendered as markdown; the incomplete tail is shown as plain text. Avoids
 * mid-stream format flicker from half-closed ** or ```.
 */
export function StreamingMarkdownContent({
  content,
  className = '',
}: StreamingMarkdownContentProps) {
  const { safe, pending } = useMemo(
    () => getStreamingMarkdownSplit(content),
    [content]
  );

  const safeHtml = useMemo(() => {
    if (!safe) return '';
    const rawHtml = marked.parse(safe, { async: false }) as string;
    return sanitizeHtml(rawHtml);
  }, [safe]);

  return (
    <div
      // break-words + [&_a]:break-all: long URLs (and other unbreakable strings)
      // inside the rendered markdown wrap at any character instead of forcing
      // the parent container to grow horizontally. Especially important on
      // mobile where description previews would otherwise overflow the
      // viewport. `break-all` on anchors specifically because URLs have no
      // natural break opportunities; `break-words` covers other content.
      className={`prose prose-sm prose-zinc dark:prose-invert max-w-none break-words [&_a]:break-all ${className}`}
    >
      {safeHtml && (
        <div dangerouslySetInnerHTML={{ __html: safeHtml }} />
      )}
      {pending ? (
        <span className="whitespace-pre-wrap break-words">{pending}</span>
      ) : null}
    </div>
  );
}
